import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import notify from '../utils/notify';
import { useApi } from '../useApi';
import { MAX_PLAYERS } from '../utils/competitionLimits';
import PlayerModal from '../PlayerModal';
import { readPlayerDraft } from '../utils/playerDraft';
import { FiCheck, FiPlus, FiEdit2, FiX, FiChevronDown, FiStar, FiSearch } from 'react-icons/fi';
import { useAuth } from '../AuthContext';
import { Permission } from '../permissions';
import './PlayerPicker.css';

// Only this modal drafts a half-typed player. See `utils/playerDraft` for why
// that is opt-in rather than everywhere.
const PLAYER_DRAFT_KEY = 'round-setup';

// Four balls to a tee time unless told otherwise. The standard wizard has no
// group-size field — groups are arranged by hand on the next step — so this is
// what the hint counts in.
const DEFAULT_GROUP_SIZE = 4;

// Adding the whole roster in one tap is a real action with a real consequence,
// so past a handful it asks first. Below that it is quicker to undo than to
// read a dialog.
const CONFIRM_ADD_ALL_OVER = 8;

// Soft tints for initials avatars, picked from the member's OWN id so a person
// is the same colour every time you see them — on the chip, in the list and in
// the summary at the top.
const TINTS = [
    { bg: '#DCE5F7', fg: '#1F4699' },
    { bg: '#F3E7C6', fg: '#6B5214' },
    { bg: '#D9EEE2', fg: '#1F5E3A' },
    { bg: '#F6DEDB', fg: '#8A2E25' },
];
const tintFor = (id) => TINTS[Math.abs(Number(id) || 0) % TINTS.length];

const initialsOf = (name) => String(name || '')
    .trim().split(/\s+/).filter(Boolean).slice(0, 2)
    .map((part) => part[0].toUpperCase()).join('') || '?';

const firstNameOf = (name) => String(name || '').trim().split(/\s+/)[0] || '';

const bySurname = (a, b) => {
    const lastA = a.name.trim().split(' ').pop().toLowerCase();
    const lastB = b.name.trim().split(' ').pop().toLowerCase();
    if (lastA === lastB) return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    return lastA.localeCompare(lastB);
};

/**
 * How the round divides up, in the words a golfer uses.
 *
 * "2 four-balls" says more than "8 players" at the moment you are deciding
 * whether to ask one more — and where it does not divide evenly, "3 groups" is
 * honest about the fact that one of them is short.
 */
export function groupHint(count, size = DEFAULT_GROUP_SIZE) {
    if (!count) return '';
    const groups = Math.ceil(count / size);
    if (size === 4 && count % 4 === 0) {
        return `${groups} four-ball${groups === 1 ? '' : 's'}`;
    }
    return `${groups} group${groups === 1 ? '' : 's'}`;
}

const Avatar = ({ player, size = 40, selected = false, className = '' }) => {
    const tint = tintFor(player.id);
    return (
        <span
            className={`pp-avatar ${className}`}
            aria-hidden="true"
            style={{
                width: size, height: size, fontSize: Math.round(size * 0.38),
                background: selected ? 'var(--pp-accent-tint)' : tint.bg,
                color: selected ? 'var(--pp-accent-text)' : tint.fg,
            }}
        >
            {initialsOf(player.name)}
        </span>
    );
};

// `suggestedPlayers` is the pool the caller thinks you're likely to want — the
// players you most often share a tee time with. They are OFFERED, never
// pre-added: the round is whoever you tap, not whoever an average of your
// history says.
// Two rows of chips, collapsed. MEASURED on an iPhone 15: seven regulars wrap
// to four rows — 184px — and that pushed the member search to within 13px of
// the footer, leaving no list under it and nothing to say the whole roster is
// searchable. A chip row is 40px and the gap 8, so two rows is 88.
const COLLAPSED_CHIPS_H = 88;

const Step3_Players = ({ data, updateData, isLocked, suggestedPlayers }) => {
    const [allPlayers, setAllPlayers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingPlayer, setEditingPlayer] = useState(null);
    const [sheetOpen, setSheetOpen] = useState(false);
    const [chipsOpen, setChipsOpen] = useState(false);
    const [chipsOverflow, setChipsOverflow] = useState(false);
    const chipsRef = useRef(null);
    // A guest half-typed before the organiser went off to another app to look
    // up a handicap. If that draft survived, the modal opens straight back on
    // it rather than making them start the name again.
    const [isCreatingPlayer, setIsCreatingPlayer] = useState(
        () => !!readPlayerDraft(PLAYER_DRAFT_KEY));
    const { authenticatedFetch } = useApi();
    const { hasPermission } = useAuth();
    const canManagePlayers = hasPermission(Permission.MANAGE_PLAYERS);

    // Determine which players are in started rounds (locked from removal)
    const startedRoundPlayerIds = useMemo(() => {
        const ids = new Set();
        data.groups?.forEach(rg => {
            if (rg.is_started) {
                rg.groups?.forEach(g => {
                    g.players?.forEach(p => ids.add(p.id));
                });
            }
        });
        return ids;
    }, [data.groups]);

    const isPlayerLocked = useCallback((player) => (
        player.is_started || startedRoundPlayerIds.has(player.id)
    ), [startedRoundPlayerIds]);

    const fetchPlayers = useCallback(async () => {
        try {
            const response = await authenticatedFetch('/players');
            if (response.ok) {
                setAllPlayers(await response.json());
            } else {
                console.error('Fetch players failed with status:', response.status);
                setAllPlayers([]);
            }
        } catch (error) {
            console.error('Failed to fetch players:', error);
            setAllPlayers([]);
        }
    }, [authenticatedFetch]);

    useEffect(() => { fetchPlayers(); }, [fetchPlayers]);

    const selectedIds = useMemo(
        () => new Set(data.players.map(p => p.id)), [data.players]);

    const handleAddPlayer = (player) => {
        if (selectedIds.has(player.id)) return;
        if (data.players.length >= MAX_PLAYERS) {
            notify.warn(`This competition is full at ${MAX_PLAYERS} players. `
                + 'Run a second competition for the rest of the field.');
            return;
        }
        updateData({ players: [...data.players, player] });
    };

    const handleRemovePlayer = (playerToRemove) => {
        if (isPlayerLocked(playerToRemove)) return;
        const updatedPlayers = data.players.filter(p => p.id !== playerToRemove.id);
        // Also drop them from every not-yet-started round's groups. Otherwise the
        // removed player lingers in the tee sheet as a phantom that later blocks
        // the round from starting (scoring needs a Round for every slotted player).
        // Started rounds are locked and left untouched.
        const prunedGroups = (data.groups || []).map(rg =>
            rg.is_started ? rg : {
                ...rg,
                groups: (rg.groups || []).map(grp => ({
                    ...grp,
                    players: (grp.players || []).filter(p => p.id !== playerToRemove.id),
                })),
            }
        );
        updateData({ players: updatedPlayers, groups: prunedGroups });
    };

    /**
     * Put the whole field back.
     *
     * "Add all" can fill a round in one tap, and until this existed the only
     * way out of a mistap was to undo it one player at a time — 47 taps to
     * correct one. It is the counterweight to that button, which is why it
     * lives with the SELECTION rather than with the list that adds.
     *
     * It ALWAYS asks, however few: adding the wrong people is visible and
     * trivially undone, where losing a field you have assembled is neither.
     * The count is in the question, so the answer is given knowing the size of
     * it.
     *
     * Players in a started round are left exactly where they are — they cannot
     * be removed one at a time either, and a bulk control is no place to make
     * an exception.
     */
    const removable = useMemo(
        () => data.players.filter(p => !isPlayerLocked(p)), [data.players, isPlayerLocked]);

    const handleRemoveAll = () => {
        if (!removable.length) return;
        // eslint-disable-next-line no-alert
        if (!window.confirm(
            `Remove all ${removable.length} players from this round?`)) return;

        const keep = new Set(data.players.filter(p => isPlayerLocked(p)).map(p => p.id));
        // The same pruning a single removal does: a player left in a group but
        // not in the round is a phantom that later blocks the round starting.
        const prunedGroups = (data.groups || []).map(rg =>
            rg.is_started ? rg : {
                ...rg,
                groups: (rg.groups || []).map(grp => ({
                    ...grp,
                    players: (grp.players || []).filter(p => keep.has(p.id)),
                })),
            }
        );
        updateData({ players: data.players.filter(p => keep.has(p.id)), groups: prunedGroups });
    };

    // One control for both states: a row you have added removes, a row you
    // have not adds. Selection is shared, so a tap here also lights the chip
    // above and moves the count at the top.
    const toggle = (player) => {
        if (selectedIds.has(player.id)) handleRemovePlayer(player);
        else handleAddPlayer(player);
    };

    const filteredPlayers = useMemo(() => allPlayers
        .filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()))
        .sort(bySurname), [allPlayers, searchTerm]);

    /**
     * Add everyone currently listed, in one go.
     *
     * Filter-aware on purpose — it adds what is on screen, not the whole
     * roster. With an empty search that is everyone; type "smi" and it is the
     * Smiths. One control covers "add the lot" and "add this handful" without
     * needing a second mode or a set of checkboxes to manage.
     */
    const handleAddAllShown = () => {
        const toAdd = filteredPlayers.filter(p => !selectedIds.has(p.id));
        if (!toAdd.length) return;

        // The one control that can walk a competition straight past the cap in
        // a single tap, so it stops at the line and says where the line is. The
        // server refuses either way; being told here, before the request, is
        // the difference between a limit and an error.
        const room = MAX_PLAYERS - data.players.length;
        if (toAdd.length > room) {
            notify.warn(room > 0
                ? `A competition holds ${MAX_PLAYERS} players and this one has `
                  + `${data.players.length}. Only ${room} more will fit — add those, `
                  + 'or run a second competition for the rest of the field.'
                : `This competition is full at ${MAX_PLAYERS} players. `
                  + 'Run a second competition for the rest of the field.');
            return;
        }
        if (toAdd.length > CONFIRM_ADD_ALL_OVER
            // eslint-disable-next-line no-alert
            && !window.confirm(`Add all ${toAdd.length} of them to this round?`)) {
            return;
        }
        updateData({ players: [...data.players, ...toAdd] });
    };

    const handleEditClick = (player) => {
        setEditingPlayer(player);
        setIsModalOpen(true);
    };

    const handleSavePlayer = async (playerData) => {
        try {
            const isNew = !playerData.id;
            const endpoint = isNew ? '/players' : `/players/${playerData.id}`;
            const response = await authenticatedFetch(endpoint, {
                method: isNew ? 'POST' : 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(playerData),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Failed to ${isNew ? 'create' : 'update'} player`);
            }

            const savedPlayer = await response.json();
            await fetchPlayers();

            if (isNew) {
                updateData({ players: [...data.players, savedPlayer] });
                setIsCreatingPlayer(false);
            } else {
                updateData({
                    players: data.players.map(p => (p.id === savedPlayer.id ? savedPlayer : p)),
                });
                setIsModalOpen(false);
                setEditingPlayer(null);
            }
        } catch (error) {
            console.error('Error saving player:', error);
            notify.error(error.message);
        }
    };

    // Is there more than the two rows on show? Measured rather than counted:
    // chips are as wide as the names in them, so four short ones and four long
    // ones wrap differently.
    useEffect(() => {
        const el = chipsRef.current;
        if (!el) return undefined;
        const measure = () => setChipsOverflow(el.scrollHeight > COLLAPSED_CHIPS_H + 1);
        measure();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, [suggestedPlayers, isLocked]);

    const selected = useMemo(() => [...data.players].sort(bySurname), [data.players]);
    const count = selected.length;
    const hint = groupHint(count);

    // Still on offer: a regular already in the round has nothing left to do,
    // so the row is what you could still add rather than a checklist.
    const regulars = (suggestedPlayers || []);
    const addableCount = filteredPlayers.filter(p => !selectedIds.has(p.id)).length;
    const hasAnyLockedPlayers = data.players.some(p => isPlayerLocked(p));

    // The cap is a fact about the competition, so it is stated where the bulk
    // control is rather than left to an alert after the tap. Once the roster is
    // bigger than the room left, the useful number is the ROOM.
    const roomLeft = MAX_PLAYERS - count;
    const isFull = roomLeft <= 0;

    return (
        <div className="wizard-step player-picker">
            {hasAnyLockedPlayers && (
                <div className="wiz-locked-banner">
                    Players with scores in started rounds cannot be removed
                </div>
            )}

            {/* ── Who's playing ─────────────────────────────────────────── */}
            <div className="pp-summary-head">
                <h3 className="pp-summary-title">Who&rsquo;s playing?</h3>
                <span className="pp-summary-count">{count} selected</span>
            </div>

            {count === 0 && (
                <div className="pp-faces">
                    {[0, 1, 2, 3].map(i => <span key={i} className="pp-face-empty" aria-hidden="true" />)}
                    <span className="pp-faces-hint">Pick your players below</span>
                </div>
            )}

            {count > 0 && count <= 4 && (
                <div className="pp-faces">
                    {selected.map(player => {
                        const locked = isPlayerLocked(player);
                        return (
                            <span key={player.id} className="pp-face">
                                <Avatar player={player} size={48} selected />
                                {!locked && (
                                    <button
                                        type="button"
                                        className="pp-face-remove"
                                        onClick={() => handleRemovePlayer(player)}
                                        aria-label={`Remove ${player.name}`}
                                    >
                                        <FiX size={12} aria-hidden="true" />
                                    </button>
                                )}
                                <span className="pp-face-name">{firstNameOf(player.name)}</span>
                            </span>
                        );
                    })}
                    <span className="pp-face">
                        <span className="pp-face-empty" aria-hidden="true" />
                        <span className="pp-face-name pp-face-name--muted">Open</span>
                    </span>
                </div>
            )}

            {count > 4 && (
                <button type="button" className="pp-stack-card" onClick={() => setSheetOpen(true)}>
                    {/* THREE faces, and in each player's OWN colour. MEASURED:
                        the design's five at 40px with a 10px overlap is 160px
                        of a ~290px card, which left "2 four-balls" to truncate
                        to "2 gro…" — and the figure is the point of the card.
                        In one flat selected tint they were also five identical
                        discs, which is a texture rather than a group. */}
                    <span className="pp-stack">
                        {selected.slice(0, 3).map(player => (
                            <Avatar key={player.id} player={player} size={40} className="pp-stack-face" />
                        ))}
                        {count > 3 && <span className="pp-stack-more">+{count - 3}</span>}
                    </span>
                    <span className="pp-stack-text">
                        <span className="pp-stack-hint">{hint}</span>
                        <span className="pp-stack-sub">Set in the next step</span>
                    </span>
                    <FiChevronDown size={18} className="pp-stack-chevron" aria-hidden="true" />
                </button>
            )}

            {/* The hint and the way back, on their own line. MEASURED: three
                controls across the title row put "Who's playing?", the count
                and this button all onto two lines each on a ~290px card. */}
            {count > 0 && (
                <div className="pp-summary-foot">
                    {count <= 4 ? <span className="pp-hint">{hint}</span> : <span />}
                    {removable.length > 1 && (
                        <button type="button" className="pp-remove-all" onClick={handleRemoveAll}>
                            Remove all
                        </button>
                    )}
                </div>
            )}

            {/* ── Your regulars ─────────────────────────────────────────── */}
            {regulars.length > 0 && !isLocked && (
                <>
                    <h4 className="pp-label">
                        <FiStar size={12} aria-hidden="true" className="pp-label-star" />
                        Your regulars
                    </h4>
                    <div
                        className={`pp-chips${chipsOpen ? ' is-open' : ''}`}
                        ref={chipsRef}
                    >
                        {regulars.map(player => {
                            const on = selectedIds.has(player.id);
                            return (
                                <button
                                    key={player.id}
                                    type="button"
                                    className={`pp-chip${on ? ' is-on' : ''}`}
                                    aria-pressed={on}
                                    onClick={() => toggle(player)}
                                >
                                    {on ? (
                                        <span className="pp-chip-tick" aria-hidden="true">
                                            <FiCheck size={16} />
                                        </span>
                                    ) : (
                                        <Avatar player={player} size={32} className="pp-chip-face" />
                                    )}
                                    {player.name}
                                </button>
                            );
                        })}
                    </div>
                    {/* Only where there is something hidden. The point of the
                        cap is the SEARCH below it: a member has to be able to
                        see that the whole roster is there to be searched
                        without scrolling to find out. */}
                    {chipsOverflow && (
                        <button
                            type="button"
                            className="pp-chips-more"
                            onClick={() => setChipsOpen(o => !o)}
                            aria-expanded={chipsOpen}
                        >
                            {chipsOpen ? 'Show fewer' : `Show all ${regulars.length}`}
                        </button>
                    )}
                </>
            )}

            {/* ── All members ───────────────────────────────────────────── */}
            <div className="pp-label-row">
                <h4 className="pp-label">All members · {allPlayers.length}</h4>
                {isFull ? (
                    <span className="pp-room pp-room--full">{`Full — ${MAX_PLAYERS} players`}</span>
                ) : (
                    <span className="pp-room-row">
                        {/* The room BESIDE the control, not instead of it:
                            once the roster is bigger than the seats left, that
                            is the number worth knowing — but taking the button
                            away would leave an organiser with no way to find
                            out why. */}
                        {addableCount > roomLeft && (
                            <span className="pp-room">{`${roomLeft} more will fit`}</span>
                        )}
                        {addableCount > 1 && (
                            <button type="button" className="pp-add-all" onClick={handleAddAllShown}>
                                {searchTerm ? `Add these ${addableCount}` : 'Add all'}
                            </button>
                        )}
                    </span>
                )}
            </div>

            {/* Sticky, so it stays reachable however far down a long roster you
                are. It sits under the section it filters rather than at the top
                of the page: it searches THIS list and nothing else. */}
            <div className="pp-search-bar">
                <div className="pp-search">
                    <FiSearch size={17} className="pp-search-icon" aria-hidden="true" />
                    <input
                        type="text"
                        className="pp-search-input"
                        placeholder={allPlayers.length
                            ? `Search all ${allPlayers.length} members`
                            : 'Search members'}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        aria-label="Search members"
                    />
                </div>
                {canManagePlayers && (
                    <button
                        type="button"
                        className="pp-guest"
                        onClick={() => setIsCreatingPlayer(true)}
                    >
                        <FiPlus size={14} aria-hidden="true" /> Guest
                    </button>
                )}
            </div>

            <div className="pp-list">
                {filteredPlayers.map(player => {
                    const on = selectedIds.has(player.id);
                    const locked = on && isPlayerLocked(player);
                    return (
                        <div key={player.id} className={`pp-row${on ? ' is-on' : ''}`}>
                            <Avatar player={player} selected={on} />
                            <span className="pp-row-text">
                                <span className="pp-row-name">{player.name}</span>
                                {/* The pill IS the edit control — a separate
                                    pencil beside it was a second 18px target
                                    for the same job. */}
                                {canManagePlayers ? (
                                    <button
                                        type="button"
                                        className="pp-hcp pp-hcp--editable"
                                        onClick={() => handleEditClick(player)}
                                        aria-label={`Edit handicap for ${player.name}`}
                                    >
                                        HCP {player.handicap}
                                        <FiEdit2 size={10} aria-hidden="true" />
                                    </button>
                                ) : (
                                    <span className="pp-hcp">HCP {player.handicap}</span>
                                )}
                            </span>
                            <button
                                type="button"
                                className={`pp-toggle${on ? ' is-on' : ''}`}
                                onClick={() => toggle(player)}
                                disabled={locked}
                                aria-label={on ? `Remove ${player.name}` : `Add ${player.name}`}
                            >
                                {on ? <FiCheck size={18} aria-hidden="true" />
                                    : <FiPlus size={18} aria-hidden="true" />}
                            </button>
                        </div>
                    );
                })}
                {!filteredPlayers.length && (
                    <p className="pp-empty">No members match “{searchTerm}”.</p>
                )}
            </div>

            {/* Everyone who is in, when there are too many to show as faces. */}
            {sheetOpen && (
                <div className="pp-sheet-scrim" onClick={() => setSheetOpen(false)}>
                    <div
                        className="pp-sheet"
                        role="dialog"
                        aria-label="Players in this round"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="pp-sheet-head">
                            <h4>{count} playing · {hint}</h4>
                            <button
                                type="button"
                                className="pp-sheet-close"
                                onClick={() => setSheetOpen(false)}
                                aria-label="Close"
                            >
                                <FiX size={18} aria-hidden="true" />
                            </button>
                        </div>
                        <div className="pp-sheet-list">
                            {selected.map(player => (
                                <div key={player.id} className="pp-row">
                                    <Avatar player={player} selected />
                                    <span className="pp-row-text">
                                        <span className="pp-row-name">{player.name}</span>
                                        <span className="pp-hcp">HCP {player.handicap}</span>
                                    </span>
                                    {!isPlayerLocked(player) && (
                                        <button
                                            type="button"
                                            className="pp-toggle is-on"
                                            onClick={() => handleRemovePlayer(player)}
                                            aria-label={`Remove ${player.name}`}
                                        >
                                            <FiX size={18} aria-hidden="true" />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <PlayerModal
                isOpen={isModalOpen}
                onClose={() => { setIsModalOpen(false); setEditingPlayer(null); }}
                onSave={handleSavePlayer}
                player={editingPlayer}
            />

            <PlayerModal
                isOpen={isCreatingPlayer}
                onClose={() => setIsCreatingPlayer(false)}
                onSave={handleSavePlayer}
                player={null}
                draftKey={PLAYER_DRAFT_KEY}
            />
        </div>
    );
};

export default Step3_Players;
